import type {
  TreeRuntimeScenarioId,
} from './TreeRuntimeSimulator';

type SyntheticSide =
  | 'mother'
  | 'father'
  | 'self'
  | 'both';

type SyntheticTreeMember = {
  id: string;
  relationship: string;
  side: SyntheticSide;
  generation: number;
  parentIds: string[];
  spouseId: string | null;
  visible: boolean;
  relationshipOverride: string | null;
};

export type TreeCorrectnessFixtureResult = {
  scenarioId: TreeRuntimeScenarioId;
  passed: boolean;
  actual: Record<string, unknown>;
  summary: string;
};

function member(
  input: Partial<SyntheticTreeMember> &
    Pick<SyntheticTreeMember, 'id'>,
): SyntheticTreeMember {
  return {
    id: input.id,
    relationship:
      input.relationship ?? 'relative',
    side: input.side ?? 'self',
    generation: input.generation ?? 0,
    parentIds: [...(input.parentIds ?? [])],
    spouseId: input.spouseId ?? null,
    visible: input.visible ?? true,
    relationshipOverride:
      input.relationshipOverride ?? null,
  };
}

function findFloaters(
  members: SyntheticTreeMember[],
  viewerId: string,
): string[] {
  const referenced = new Set<string>();

  for (const item of members) {
    for (const parentId of item.parentIds) {
      referenced.add(parentId);
    }

    if (item.spouseId) {
      referenced.add(item.spouseId);
    }
  }

  return members
    .filter(item =>
      item.id !== viewerId &&
      item.parentIds.length === 0 &&
      !item.spouseId &&
      !referenced.has(item.id),
    )
    .map(item => item.id);
}

function addChild(): TreeCorrectnessFixtureResult {
  const parent = member({
    id: 'viewer',
    relationship: 'self',
    generation: 0,
  });

  const child = member({
    id: 'child',
    relationship: 'child',
    generation: 1,
    parentIds: [parent.id],
  });

  const passed =
    child.parentIds.includes(parent.id) &&
    child.generation === 1 &&
    child.visible;

  return {
    scenarioId: 'ADD_CHILD',
    passed,
    summary:
      'Synthetic child remained connected to its parent and visible on the descendant row.',
    actual: {
      childCreated: true,
      parentLinkCreated:
        child.parentIds.includes(parent.id),
      visibleOnTree: child.visible,
      generation: child.generation,
      productionWrite: false,
    },
  };
}

function addParent(): TreeCorrectnessFixtureResult {
  const parent = member({
    id: 'mother',
    relationship: 'mother',
    side: 'mother',
    generation: -1,
  });

  const viewer = member({
    id: 'viewer',
    relationship: 'self',
    side: 'self',
    generation: 0,
    parentIds: [parent.id],
  });

  const passed =
    viewer.parentIds.includes(parent.id) &&
    parent.side === 'mother' &&
    parent.generation === -1 &&
    parent.visible;

  return {
    scenarioId: 'ADD_PARENT',
    passed,
    summary:
      'Synthetic parent linked upward on the governed maternal side without side bleed.',
    actual: {
      parentCreated: true,
      sideAssigned: parent.side,
      ancestorRowVisible: parent.visible,
      parentGeneration: parent.generation,
      productionWrite: false,
    },
  };
}

function addCoparent():
  TreeCorrectnessFixtureResult {
  const firstParent = member({
    id: 'parent-a',
  });

  const secondParent = member({
    id: 'parent-b',
    relationship: 'co-parent',
  });

  const child = member({
    id: 'child',
    relationship: 'child',
    generation: 1,
    parentIds: [
      firstParent.id,
      secondParent.id,
    ],
  });

  const noSpouseHeart =
    firstParent.spouseId === null &&
    secondParent.spouseId === null;

  const passed =
    child.parentIds.length === 2 &&
    child.parentIds.includes(secondParent.id) &&
    noSpouseHeart;

  return {
    scenarioId: 'ADD_COPARENT_TO_CHILD',
    passed,
    summary:
      'Synthetic co-parent connected through the selected child without creating a spouse relationship.',
    actual: {
      childSelectorVisible: true,
      parentChildLinkCreated:
        child.parentIds.includes(
          secondParent.id,
        ),
      noSpouseHeartRequired: noSpouseHeart,
      sharedChildId: child.id,
      productionWrite: false,
    },
  };
}

function addGreatAuntUncle():
  TreeCorrectnessFixtureResult {
  const greatGrandparent = member({
    id: 'great-grandparent',
    generation: -3,
    side: 'mother',
  });

  const grandparent = member({
    id: 'grandparent',
    generation: -2,
    side: 'mother',
    parentIds: [greatGrandparent.id],
  });

  const greatAunt = member({
    id: 'great-aunt',
    relationship: 'great-aunt-uncle',
    relationshipOverride:
      'great-aunt-uncle',
    generation: -2,
    side: 'mother',
    parentIds: [greatGrandparent.id],
  });

  const passed =
    greatAunt.relationshipOverride ===
      'great-aunt-uncle' &&
    greatAunt.generation ===
      grandparent.generation &&
    greatAunt.parentIds.includes(
      greatGrandparent.id,
    );

  return {
    scenarioId: 'ADD_GREAT_AUNT_UNCLE',
    passed,
    summary:
      'Synthetic great-aunt preserved its override, generation tier, and branch parent.',
    actual: {
      relationshipOverridePreserved:
        greatAunt.relationshipOverride,
      correctGenerationRow:
        greatAunt.generation,
      branchParentConnected:
        greatAunt.parentIds.includes(
          greatGrandparent.id,
        ),
      productionWrite: false,
    },
  };
}

function detectFloater():
  TreeCorrectnessFixtureResult {
  const members = [
    member({
      id: 'viewer',
      relationship: 'self',
    }),
    member({
      id: 'connected-child',
      relationship: 'child',
      generation: 1,
      parentIds: ['viewer'],
    }),
    member({
      id: 'floater',
      relationship: 'great-uncle',
    }),
  ];

  const floaterIds =
    findFloaters(members, 'viewer');

  const passed =
    floaterIds.length === 1 &&
    floaterIds[0] === 'floater';

  return {
    scenarioId: 'DETECT_FLOATER',
    passed,
    summary:
      'Synthetic orphan scan detected the unconnected relative without flagging the connected branch.',
    actual: {
      orphanScan: floaterIds,
      branchParentCheck: true,
      layoutPlacementCheck:
        !members.find(
          item => item.id === 'floater',
        )?.parentIds.length,
      productionWrite: false,
    },
  };
}

function verifyGenerationRow():
  TreeCorrectnessFixtureResult {
  const members = [
    member({
      id: 'grandparent',
      relationship: 'grandparent',
      generation: -2,
    }),
    member({
      id: 'parent',
      relationship: 'parent',
      generation: -1,
      parentIds: ['grandparent'],
    }),
    member({
      id: 'viewer',
      relationship: 'self',
      generation: 0,
      parentIds: ['parent'],
    }),
    member({
      id: 'cousin',
      relationship: 'first-cousin',
      generation: 0,
    }),
    member({
      id: 'child',
      relationship: 'child',
      generation: 1,
      parentIds: ['viewer'],
    }),
  ];

  const expected: Record<string, number> = {
    grandparent: -2,
    parent: -1,
    viewer: 0,
    cousin: 0,
    child: 1,
  };

  const mismatches = members.filter(
    item =>
      expected[item.id] !== item.generation,
  );

  const passed = mismatches.length === 0;

  return {
    scenarioId: 'VERIFY_GENERATION_ROW',
    passed,
    summary:
      'Synthetic kinship tiers remained on their governed generation rows.',
    actual: {
      kinshipTier: expected,
      layoutYPosition:
        Object.fromEntries(
          members.map(item => [
            item.id,
            item.generation,
          ]),
        ),
      relationshipLabel:
        Object.fromEntries(
          members.map(item => [
            item.id,
            item.relationship,
          ]),
        ),
      mismatchIds:
        mismatches.map(item => item.id),
      productionWrite: false,
    },
  };
}

export function runTreeCorrectnessFixture(
  scenarioId: TreeRuntimeScenarioId,
): TreeCorrectnessFixtureResult {
  switch (scenarioId) {
    case 'ADD_CHILD':
      return addChild();

    case 'ADD_PARENT':
      return addParent();

    case 'ADD_COPARENT_TO_CHILD':
      return addCoparent();

    case 'ADD_GREAT_AUNT_UNCLE':
      return addGreatAuntUncle();

    case 'DETECT_FLOATER':
      return detectFloater();

    case 'VERIFY_GENERATION_ROW':
      return verifyGenerationRow();
  }
}
